// Remembered admin phones (jobq-api "Remembered admin phones", since 2026-10-20): the phones an Admin's
// unlock is remembered on for 30 days (utils/adminRemember.js), listed in User Management with Sign out.
// ONE copy for the desktop page (components/RememberedPhones.jsx on pages/UserManagementPage.jsx) and the
// phone (mobile/screens/UsersScreen.jsx). Both routes need the admin session's PIN (X-Admin-Pin).

import { needsAdminPin, securityFetch } from '../../utils/securityApi';

/**
 * GET /admin_devices: {ok, rows, needsPin, unavailable, message}. Never throws. `unavailable` = this JobQ
 * can't remember phones (no route yet: 404; not set up: 503): the list is simply not shown.
 */
export async function loadAdminDevices({ includeEnded = false } = {}) {
  const r = await securityFetch(`/admin_devices${includeEnded ? '?include_ended=true' : ''}`);
  const rows = r.ok && Array.isArray(r.body) ? r.body : (r.ok && r.body && Array.isArray(r.body.devices) ? r.body.devices : []);
  return {
    ok: r.ok, rows, needsPin: needsAdminPin(r), unavailable: r.status === 404 || r.status === 503,
    message: r.ok ? null : r.message,
  };
}

/** POST /admin_devices/{id}/sign_out: {ok, message}. Never throws. */
export async function signOutAdminDevice(deviceId) {
  const r = await securityFetch(`/admin_devices/${encodeURIComponent(deviceId)}/sign_out`, { method: 'POST', body: '{}' });
  return { ok: r.ok, message: r.ok ? null : r.message };
}

/** One person's phones, live first, newest first. */
export function devicesForUser(rows, userId) {
  return (rows || [])
    .filter((d) => String(d.user_id) === String(userId))
    .sort((a, b) => (Number(Boolean(b.live)) - Number(Boolean(a.live)))
      || String(b.created_at || '').localeCompare(String(a.created_at || '')));
}

const ENDED = {
  signed_out: 'Signed out',
  signed_out_on_phone: 'Signed out on the phone',
  expired: '30 days were up',
  unknown: 'Ended',
  pin_changed: 'Ended: PIN changed',
  deactivated: 'Ended: user made inactive',
  inactive: 'Ended: user made inactive',
  role_changed: 'Ended: no longer an Admin',
  not_admin: 'Ended: no longer an Admin',
  user_deleted: 'Ended: user deleted',
  replaced: 'Replaced by a newer phone',
};

const day = (iso) => {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const d = new Date(t);
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
};

/** The line under a phone: "Until Nov 19, 3:42 PM · last used Oct 21, 7:02 AM" or why it ended. */
export function deviceStatusText(d) {
  if (!d) return '';
  if (d.live) {
    return [`Until ${day(d.expires_at)}`, d.last_used_at ? `last used ${day(d.last_used_at)}` : 'not used since'].join(' · ');
  }
  // revoked_reason once signed out; else the API's `state` (the refusal it would get: e.g. pin_changed)
  const why = ENDED[d.revoked_reason] || ENDED[d.state] || (d.revoked_at ? 'Ended' : 'Expired');
  return [why, d.revoked_by ? `by ${d.revoked_by}` : null, d.revoked_at ? day(d.revoked_at) : day(d.expires_at)]
    .filter(Boolean).join(' · ');
}
